/**
 * ProjectSearchBar Component
 * 
 * Landing page search bar for discovering projects
 * - Search by project name, RERA ID, city, developer
 * - Shows results or "Request Project" flow if not found
 * - Alerts backend team when new project requested
 */

import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, MapPin, Building2, FileText, AlertCircle, CheckCircle, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Popular cities for quick filters
const POPULAR_CITIES = ['Mumbai', 'Pune', 'Bangalore', 'Gurugram', 'Noida', 'Hyderabad', 'Chennai'];

export default function ProjectSearchBar({ onProjectSelect, onOpenAuth, user }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestForm, setRequestForm] = useState({
    project_name: '',
    city: '',
    developer_name: '',
    rera_id: '',
    requester_email: '',
    requester_phone: '',
    additional_info: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Debounced search
  useEffect(() => {
    if (searchQuery.length >= 2) {
      const timer = setTimeout(() => {
        performSearch();
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
      setHasSearched(false);
    }
  }, [searchQuery]);

  const performSearch = async () => {
    setIsSearching(true);
    try {
      const res = await axios.get(`${API}/api/projects`, {
        params: { search: searchQuery, limit: 10 }
      });
      setSearchResults(res.data || []);
      setHasSearched(true);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleCityFilter = (city) => {
    setSearchQuery(city);
  };

  const handleProjectClick = (project) => {
    if (onProjectSelect) {
      onProjectSelect(project);
    }
  };

  const handleRequestProject = () => {
    // Pre-fill with search query
    setRequestForm(prev => ({
      ...prev,
      project_name: searchQuery,
      requester_email: user?.email || ''
    }));
    setShowRequestDialog(true);
  };

  const submitProjectRequest = async () => {
    if (!requestForm.project_name || !requestForm.city) {
      toast.error('Please provide project name and city');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/api/project-requests`, {
        ...requestForm,
        requester_email: requestForm.requester_email || user?.email || '',
        requester_phone: requestForm.requester_phone || user?.phone || '',
        requester_id: user?.id,
        status: 'pending',
        requested_at: new Date().toISOString()
      });
      
      toast.success('Request submitted! FBC team will add this project soon.');
      setShowRequestDialog(false);
      setRequestForm({
        project_name: '',
        city: '',
        developer_name: '',
        rera_id: '',
        requester_email: '',
        requester_phone: '',
        additional_info: ''
      });
    } catch (err) {
      console.error('Request error:', err);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto">
      {/* Main Search Bar */}
      <div className="relative">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by project name, RERA ID, city, or developer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 text-base"
              data-testid="project-search-input"
            />
          </div>
          <Button 
            size="lg" 
            onClick={performSearch}
            disabled={searchQuery.length < 2}
            data-testid="project-search-button"
          >
            {isSearching ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Search'}
          </Button>
        </div>

        {/* Search Results Dropdown */}
        {hasSearched && (
          <Card className="absolute top-full left-0 right-0 mt-2 z-50 max-h-96 overflow-auto shadow-lg">
            <CardContent className="p-2">
              {searchResults.length > 0 ? (
                <div className="space-y-1">
                  {searchResults.map((project) => (
                    <div
                      key={project.id}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => handleProjectClick(project)}
                      data-testid={`search-result-${project.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium">{project.name}</div>
                          <div className="text-sm text-muted-foreground flex items-center gap-2">
                            <MapPin className="h-3 w-3" />
                            {project.city || project.location}
                            {project.developer_name && (
                              <>
                                <span>•</span>
                                {project.developer_name}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {project.rera_id && (
                          <Badge variant="secondary" className="text-xs">
                            RERA: {project.rera_id.slice(0, 12)}...
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-xs">
                          View Info
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* No Results - Show Request Option */
                <div className="p-6 text-center">
                  <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <h3 className="font-semibold text-lg mb-1">Your project may not be on FBC yet</h3>
                  <p className="text-muted-foreground mb-4">
                    "{searchQuery}" isn't listed. It can still be part of FBC: ask us to open it and bring your fellow buyers along.
                  </p>
                  <Button onClick={() => window.dispatchEvent(new CustomEvent('fbc-request-project', { detail: { name: searchQuery } }))} data-testid="request-community-button">
                    <Plus className="h-4 w-4 mr-2" />
                    Request your project
                  </Button>
                  <div>
                    <Button variant="link" size="sm" className="text-xs mt-2" onClick={handleRequestProject} data-testid="request-project-button">
                      Just researching this project? Ask us to compile its RERA info
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Popular Cities Quick Filters */}
      <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
        <span className="text-sm text-muted-foreground">Popular:</span>
        {POPULAR_CITIES.map((city) => (
          <Button
            key={city}
            variant="ghost"
            size="sm"
            className="text-sm"
            onClick={() => handleCityFilter(city)}
          >
            {city}
          </Button>
        ))}
      </div>

      {/* Request Project Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request Project Information</DialogTitle>
            <DialogDescription>
              Fill in the details and we'll compile the RERA and project information for you.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="project_name">Project Name *</Label>
              <Input
                id="project_name"
                value={requestForm.project_name}
                onChange={(e) => setRequestForm(prev => ({ ...prev, project_name: e.target.value }))}
                placeholder="e.g., Lodha Park"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City *</Label>
                <Select 
                  value={requestForm.city} 
                  onValueChange={(value) => setRequestForm(prev => ({ ...prev, city: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    {POPULAR_CITIES.map(city => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="developer_name">Developer Name</Label>
                <Input
                  id="developer_name"
                  value={requestForm.developer_name}
                  onChange={(e) => setRequestForm(prev => ({ ...prev, developer_name: e.target.value }))}
                  placeholder="e.g., Lodha Group"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rera_id">RERA ID (if known)</Label>
              <Input
                id="rera_id"
                value={requestForm.rera_id}
                onChange={(e) => setRequestForm(prev => ({ ...prev, rera_id: e.target.value }))}
                placeholder="e.g., P52000001234"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="maharera_url">MahaRERA project page link (speeds up onboarding)</Label>
              <Input
                id="maharera_url"
                value={requestForm.maharera_url || ''}
                onChange={(e) => setRequestForm(prev => ({ ...prev, maharera_url: e.target.value }))}
                placeholder="https://maharerait.maharashtra.gov.in/public/project/view/…"
                data-testid="request-maharera-url"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="requester_email">Your Email *</Label>
                <Input
                  id="requester_email"
                  type="email"
                  value={requestForm.requester_email}
                  onChange={(e) => setRequestForm(prev => ({ ...prev, requester_email: e.target.value }))}
                  placeholder="you@email.com"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="requester_phone">Phone (optional)</Label>
                <Input
                  id="requester_phone"
                  value={requestForm.requester_phone}
                  onChange={(e) => setRequestForm(prev => ({ ...prev, requester_phone: e.target.value }))}
                  placeholder="+91 98765 43210"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="additional_info">Additional Information</Label>
              <Textarea
                id="additional_info"
                value={requestForm.additional_info}
                onChange={(e) => setRequestForm(prev => ({ ...prev, additional_info: e.target.value }))}
                placeholder="Any other details that might help us locate the project..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>
              Cancel
            </Button>
            <Button onClick={submitProjectRequest} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
